import { useState } from "react";
import { CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  binderyApi,
  type OfflineThesisPaymentMethod,
  type ThesisPrintRequest,
} from "@/lib/api/bindery";
import { formatDate, formatMoney, THESIS_PAYMENT_METHOD_LABELS } from "../binderyFormatters";

interface Props {
  req: ThesisPrintRequest;
  onChange: (next: ThesisPrintRequest) => void;
}

const OFFLINE_METHODS: OfflineThesisPaymentMethod[] = ["CASH", "BANK_TRANSFER", "POS", "OTHER"];

export function StaffPaymentCard({ req, onChange }: Props) {
  const toast = useToast();
  const [method, setMethod] = useState<OfflineThesisPaymentMethod>("CASH");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (req.paidAt) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-emerald-900">
            <CheckCircle2 className="h-4 w-4" /> Payment recorded
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-emerald-900">
          <p>
            <span className="font-semibold">{formatMoney(req.cost)}</span> paid on{" "}
            {formatDate(req.paidAt)}
          </p>
          {req.paymentMethod && (
            <p>Method: {THESIS_PAYMENT_METHOD_LABELS[req.paymentMethod] ?? req.paymentMethod}</p>
          )}
          {req.paymentReference && <p>Reference: {req.paymentReference}</p>}
          {req.paymentNote && <p className="text-emerald-800/80">{req.paymentNote}</p>}
        </CardContent>
      </Card>
    );
  }

  if (req.status !== "APPROVED") {
    return null;
  }

  const submit = async () => {
    setBusy(true);
    try {
      const next = await binderyApi.thesis.markPaid(req.id, {
        method,
        reference: reference.trim() || undefined,
        note: note.trim() || undefined,
      });
      onChange(next);
      toast.success("Payment recorded. You can now start printing.");
      setReference("");
      setNote("");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to record payment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-amber-900">
          <CreditCard className="h-4 w-4" /> Record payment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-amber-900">
          Payment of <span className="font-semibold">{formatMoney(req.cost)}</span> is required
          before printing can start.
        </p>
        {req.invoiceId && (
          <p className="rounded border border-amber-300/60 bg-white/60 px-3 py-2 text-xs text-amber-900">
            Invoice #<span className="font-mono">{req.invoiceId.slice(-8)}</span> is pending. The
            student can pay online with Paystack from their thesis page, or you can record an
            in-person payment below.
          </p>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Method</Label>
            <Select
              value={method}
              onValueChange={(v) => setMethod(v as OfflineThesisPaymentMethod)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OFFLINE_METHODS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {THESIS_PAYMENT_METHOD_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="pay-ref">Reference (optional)</Label>
            <Input
              id="pay-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Receipt no. / transfer ref"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="pay-note">Note (optional)</Label>
          <Textarea id="pay-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <Button disabled={busy} onClick={submit}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Confirm payment received
        </Button>
      </CardContent>
    </Card>
  );
}
